// =============================================================================
// staticSite/wpRestPublic — odczyt PUBLICZNYCH danych WordPressa przez REST
// (bez auth): wpisy, strony, kategorie. Zrodlo prawdy dla migracji — to, co
// jest na WP dzisiaj, a nie to, co panel zsynchronizowal kiedys do wp_posts.
// Paginacja po naglowku X-WP-TotalPages (max 20 stron x 100 = 2000 wpisow).
// =============================================================================

export interface WpRestPost {
  id: number;
  link: string;
  slug: string;
  title: string;
  content: string;
  excerpt: string;
  date: string;
  modified: string;
  categories: number[];
  featuredImageUrl: string | null;
}

export interface WpRestPage {
  id: number;
  link: string;
  slug: string;
  title: string;
  content: string;
  excerpt: string;
  date: string;
  modified: string;
}

export interface WpRestCategory {
  id: number;
  slug: string;
  name: string;
  description: string;
  link: string;
  count: number;
}

const TIMEOUT_MS = 20_000;

async function getJson<T>(url: string): Promise<{ data: T; totalPages: number }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`HTTP ${res.status} ${url}`);
    const totalPages = Number(res.headers.get("x-wp-totalpages") || 1) || 1;
    return { data: (await res.json()) as T, totalPages };
  } finally {
    clearTimeout(timer);
  }
}

async function paged<T>(base: string, endpoint: string, params: Record<string, string>, perPage = 100): Promise<T[]> {
  const all: T[] = [];
  for (let page = 1; page <= 20; page++) {
    const qs = new URLSearchParams({ ...params, per_page: String(perPage), page: String(page) });
    const { data, totalPages } = await getJson<T[]>(`${base}/wp-json/wp/v2/${endpoint}?${qs}`);
    all.push(...(Array.isArray(data) ? data : []));
    if (page >= totalPages || (Array.isArray(data) && data.length < perPage)) break;
  }
  return all;
}

export function decodeEntities(s: string): string {
  return (s || "")
    .replace(/&#8211;/g, "–").replace(/&#8212;/g, "—").replace(/&#8217;/g, "’").replace(/&#8216;/g, "‘")
    .replace(/&#8220;/g, "“").replace(/&#8221;/g, "”").replace(/&#8230;/g, "…")
    .replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#039;/g, "'").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ");
}

/** Wpisy opublikowane + featured image (przez _embed). */
export async function fetchWpPosts(siteUrl: string): Promise<WpRestPost[]> {
  const base = siteUrl.replace(/\/$/, "");
  const raw = await paged<any>(base, "posts", { status: "publish", _embed: "wp:featuredmedia" }, 50);
  return raw.map((p) => ({
    id: p.id,
    link: p.link || "",
    slug: p.slug || "",
    title: decodeEntities(p.title?.rendered || ""),
    content: p.content?.rendered || "",
    excerpt: p.excerpt?.rendered || "",
    date: p.date_gmt ? `${p.date_gmt}Z` : p.date || "",
    modified: p.modified_gmt ? `${p.modified_gmt}Z` : p.modified || "",
    categories: Array.isArray(p.categories) ? p.categories : [],
    featuredImageUrl: p._embedded?.["wp:featuredmedia"]?.[0]?.source_url || null,
  }));
}

export async function fetchWpPages(siteUrl: string): Promise<WpRestPage[]> {
  const base = siteUrl.replace(/\/$/, "");
  const raw = await paged<any>(base, "pages", { status: "publish" });
  return raw.map((p) => ({
    id: p.id,
    link: p.link || "",
    slug: p.slug || "",
    title: decodeEntities(p.title?.rendered || ""),
    content: p.content?.rendered || "",
    excerpt: p.excerpt?.rendered || "",
    date: p.date_gmt ? `${p.date_gmt}Z` : p.date || "",
    modified: p.modified_gmt ? `${p.modified_gmt}Z` : p.modified || "",
  }));
}

export async function fetchWpCategories(siteUrl: string): Promise<WpRestCategory[]> {
  const base = siteUrl.replace(/\/$/, "");
  const raw = await paged<any>(base, "categories", { hide_empty: "true" });
  return raw.map((c) => ({
    id: c.id,
    slug: c.slug || "",
    name: decodeEntities(c.name || ""),
    description: decodeEntities((c.description || "").replace(/<[^>]+>/g, "")),
    link: c.link || "",
    count: Number(c.count || 0),
  }));
}

/** Minimalne czyszczenie content.rendered: skrypty + komentarze blokow. Linki nietkniete. */
export function cleanWpContent(html: string): string {
  return (html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .trim();
}
